import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { createInvoicePartPasteCellReplaceKeyHandler, type InvoicePartGridNumericKeyPrefix } from './invoicePartGridPasteCellReplace';
import { useLineItemGridCellKeyboardNav } from './useLineItemGridCellKeyboardNav';
import {
    LINE_ITEM_PASTE_COL_FIELDS,
    useInvoicePartGridSpreadsheetSelection,
    type UseInvoicePartGridSpreadsheetSelectionArgs,
} from './useLineItemsSpreadsheetSelection';

export type UseInvoicePartGridInteractionsArgs = UseInvoicePartGridSpreadsheetSelectionArgs & {
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues?: Dispatch<SetStateAction<Record<string, string>>>;
    numericKeyPrefix: InvoicePartGridNumericKeyPrefix;
};

/** Spreadsheet selection + Tab/Enter/arrow navigation + type-to-replace for one invoice part grid (line / freight / misc / ignored). */
export function useInvoicePartGridInteractions(args: UseInvoicePartGridInteractionsArgs) {
    const { setEditingNumericField, setEditingNumericValues, numericKeyPrefix, ...spreadsheetArgs } = args;
    const spreadsheet = useInvoicePartGridSpreadsheetSelection(spreadsheetArgs);
    const pasteColFields = spreadsheetArgs.pasteColFields ?? LINE_ITEM_PASTE_COL_FIELDS;

    const onPasteCellReplaceKey = useMemo(
        () =>
            createInvoicePartPasteCellReplaceKeyHandler({
                getContainer: () => spreadsheet.containerRef.current,
                pasteColFields,
                updateCell: spreadsheetArgs.updateCell,
                numericKeyPrefix,
                setEditingNumericField,
                setEditingNumericValues,
            }),
        [
            numericKeyPrefix,
            pasteColFields,
            setEditingNumericField,
            setEditingNumericValues,
            spreadsheet.containerRef,
            spreadsheetArgs.updateCell,
        ]
    );

    useLineItemGridCellKeyboardNav({
        containerRef: spreadsheet.containerRef,
        lineItemCount: spreadsheetArgs.rowCount,
        setEditingNumericField,
        isReadOnly: spreadsheetArgs.isReadOnly,
        onPasteCellReplaceKey,
    });

    return spreadsheet;
}
